import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Linkedin, Loader2, Mail, Phone, RefreshCw, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DuplicateManager from "@/components/common/DuplicateManager";
import { candidateMergeConfig, CandidateForMerge } from "@/config/candidateMergeConfig";
import { EntityMergeService } from "@/services/entityMergeService";
import { fetchFreshEntityData } from "@/services/entityFetcher";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Candidate } from "@/types/candidate";
import {
	DuplicateGroup,
	DuplicateReason,
	groupDuplicateCandidates,
	normalizeEmail,
	normalizeLinkedIn,
	normalizePhone,
} from "@/utils/candidate/duplicateDetection";

const SCAN_FIELDS = "id, name, email, phone, linkedin, function, location, status, created_at, updated_at";
const PAGE_SIZE = 1000;

const fetchAllCandidates = async (): Promise<Candidate[]> => {
	const all: Candidate[] = [];
	for (let from = 0; ; from += PAGE_SIZE) {
		const { data, error } = await supabase
			.from("candidates")
			.select(SCAN_FIELDS)
			.order("id")
			.range(from, from + PAGE_SIZE - 1);

		if (error)
			throw error;

		all.push(...((data || []) as unknown as Candidate[]));
		if (!data || data.length < PAGE_SIZE)
			break;
	}
	return all;
};

// Values shared by at least two candidates of the group, used to highlight what matched
const getSharedValues = (group: DuplicateGroup) => {
	const counts = new Map<string, number>();
	const add = (reason: DuplicateReason, value: string | null) => {
		if (value)
			counts.set(`${reason}:${value}`, (counts.get(`${reason}:${value}`) || 0) + 1);
	};

	group.candidates.forEach(candidate => {
		add("email", normalizeEmail(candidate.email));
		add("phone", normalizePhone(candidate.phone));
		add("linkedin", normalizeLinkedIn(candidate.linkedin));
	});

	return (reason: DuplicateReason, value: string | null) => !!value && (counts.get(`${reason}:${value}`) || 0) > 1;
};

const CandidateDuplicates = () => {
	usePageTitle("Duplicate finder");
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [candidates, setCandidates] = useState<Candidate[]>([]);
	const [isScanning, setIsScanning] = useState(true);
	const [scanError, setScanError] = useState<string | null>(null);

	const [isMergeOpen, setIsMergeOpen] = useState(false);
	const [mergeItems, setMergeItems] = useState<CandidateForMerge[]>([]);
	const [isLoadingFreshData, setIsLoadingFreshData] = useState(false);

	const scan = useCallback(async () => {
		setIsScanning(true);
		setScanError(null);
		try {
			setCandidates(await fetchAllCandidates());
		} catch (error) {
			console.error("Error scanning for duplicate candidates:", error);
			setScanError(error instanceof Error ? error.message : String(error));
			setCandidates([]);
		} finally {
			setIsScanning(false);
		}
	}, []);

	useEffect(() => {
		scan();
	}, [scan]);

	const groups = useMemo(() => groupDuplicateCandidates(candidates), [candidates]);
	const duplicateCount = useMemo(() => groups.reduce((total, group) => total + group.candidates.length, 0), [groups]);

	const closeMergeModal = useCallback(() => {
		setIsMergeOpen(false);
		setMergeItems([]);
		setIsLoadingFreshData(false);
	}, []);

	// Same flow as useDuplicateManager: load fresh, complete records before merging
	const openMergeModal = async (group: DuplicateGroup) => {
		setMergeItems([]);
		setIsMergeOpen(true);
		setIsLoadingFreshData(true);
		try {
			const freshData = await fetchFreshEntityData<CandidateForMerge>("candidates", group.candidates.map(candidate => candidate.id));
			if (freshData.length < 2)
				throw new Error(t("candidates.duplicateFinder.loadError"));
			setMergeItems(freshData);
		} catch (error) {
			toast({
				title: t("candidates.duplicateFinder.mergeError"),
				description: error instanceof Error ? error.message : t("candidates.duplicateFinder.loadError"),
				variant: "destructive",
			});
			closeMergeModal();
		} finally {
			setIsLoadingFreshData(false);
		}
	};

	const handleMerge = async (merged: CandidateForMerge, sources: CandidateForMerge[]) => {
		const result = await EntityMergeService.mergeCandidates(merged, sources);
		if (!result.success) {
			toast({
				title: t("candidates.duplicateFinder.mergeError"),
				description: result.error,
				variant: "destructive",
			});
			// Rethrow so the merge modal stays open
			throw new Error(result.error || t("candidates.duplicateFinder.mergeError"));
		}

		toast({
			title: t("candidates.duplicateFinder.mergeSuccess"),
			description: t("candidates.duplicateFinder.mergeSuccessDesc", { n: sources.length }),
		});

		// Replace the merged sources by the surviving candidate so the list stays accurate
		// without rescanning. No redirectPath is returned: the user stays on this page.
		const sourceIds = new Set(sources.map(source => source.id));
		const { data: survivor } = await supabase
			.from("candidates")
			.select(SCAN_FIELDS)
			.eq("id", result.mergedId)
			.maybeSingle();

		setCandidates(previous => {
			const remaining = previous.filter(candidate => !sourceIds.has(candidate.id) && candidate.id !== result.mergedId);
			return survivor ? [...remaining, survivor as unknown as Candidate] : remaining;
		});
	};

	const renderGroup = (group: DuplicateGroup) => {
		const isShared = getSharedValues(group);
		const sharedClass = "font-medium text-amber-700";

		return (
			<div key={group.key} className="flex items-stretch gap-4 border rounded-sm bg-card p-3">
				<div className="flex flex-col gap-2 w-36 shrink-0">
					<div className="flex items-center gap-1 text-sm font-medium">
						<Users className="h-4 w-4" />
						{t("candidates.duplicateFinder.groupSize", { n: group.candidates.length })}
					</div>
					<div className="flex flex-wrap gap-1">
						{group.reasons.map(reason => (
							<Badge key={reason} variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
								{t(`candidates.duplicateFinder.reasons.${reason}`)}
							</Badge>
						))}
					</div>
				</div>

				<div className="flex flex-1 flex-wrap gap-2 min-w-0">
					{group.candidates.map(candidate => (
						<div key={candidate.id} className="w-64 border rounded-sm p-2 text-xs space-y-1 bg-background">
							<Link to={`/candidates/${candidate.id}`} className="block text-sm font-medium text-blue-500 hover:underline truncate">
								{candidate.name || t("candidates.duplicateFinder.unnamed")}
							</Link>
							{candidate.function && (
								<div className="text-muted-foreground truncate">{candidate.function}</div>
							)}
							{candidate.email && (
								<div className={`flex items-center gap-1 ${isShared("email", normalizeEmail(candidate.email)) ? sharedClass : ""}`}>
									<Mail className="h-3 w-3 shrink-0" />
									<span className="truncate">{candidate.email}</span>
								</div>
							)}
							{candidate.phone && (
								<div className={`flex items-center gap-1 ${isShared("phone", normalizePhone(candidate.phone)) ? sharedClass : ""}`}>
									<Phone className="h-3 w-3 shrink-0" />
									<span className="truncate">{candidate.phone}</span>
								</div>
							)}
							{candidate.linkedin && (
								<div className={`flex items-center gap-1 ${isShared("linkedin", normalizeLinkedIn(candidate.linkedin)) ? sharedClass : ""}`}>
									<Linkedin className="h-3 w-3 shrink-0" />
									<span className="truncate">{candidate.linkedin}</span>
								</div>
							)}
							{candidate.created_at && (
								<div className="text-muted-foreground">
									{t("candidates.duplicateFinder.created", { date: new Date(candidate.created_at).toLocaleDateString() })}
								</div>
							)}
						</div>
					))}
				</div>

				<div className="flex items-center shrink-0">
					<Button size="sm" variant="outline" onClick={() => openMergeModal(group)} className="flex items-center gap-1">
						<Users className="h-4 w-4" />
						{t("candidates.duplicateFinder.merge", { n: group.candidates.length })}
					</Button>
				</div>
			</div>
		);
	};

	return (
		<div>
			<PageHeader
				title={t("candidates.duplicateFinder.title")}
				description={t("candidates.duplicateFinder.description")}
				actions={
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => navigate("/candidates")} className="flex items-center gap-2">
							<ArrowLeft className="h-4 w-4" />
							{t("candidates.duplicateFinder.back")}
						</Button>
						<Button variant="outline" onClick={scan} disabled={isScanning} className="flex items-center gap-2">
							<RefreshCw className={`h-4 w-4 ${isScanning ? "animate-spin" : ""}`} />
							{t("candidates.duplicateFinder.rescan")}
						</Button>
					</div>
				}
			/>

			{isScanning ? (
				<div className="flex items-center justify-center gap-3 py-16 text-muted-foreground">
					<Loader2 className="h-5 w-5 animate-spin" />
					<span>{t("candidates.duplicateFinder.scanning")}</span>
				</div>
			) : scanError ? (
				<div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm p-3">
					{t("candidates.duplicateFinder.scanError")}: {scanError}
				</div>
			) : groups.length === 0 ? (
				<div className="py-16 text-center text-muted-foreground">
					{t("candidates.duplicateFinder.empty", { scanned: candidates.length })}
				</div>
			) : (
				<div className="space-y-3">
					<p className="text-sm text-muted-foreground">
						{t("candidates.duplicateFinder.summary", { groups: groups.length, duplicates: duplicateCount, scanned: candidates.length })}
					</p>
					{groups.map(renderGroup)}
				</div>
			)}

			<DuplicateManager<CandidateForMerge>
				open={isMergeOpen}
				onClose={closeMergeModal}
				selectedItems={mergeItems}
				onMerge={handleMerge}
				entityLabel="candidates"
				mergeConfig={candidateMergeConfig}
				isLoadingFreshData={isLoadingFreshData}
			/>
		</div>
	);
};

export default CandidateDuplicates;
